import React, { useState } from 'react';
import {
  IAuditoriaContaTransitoriaResultado,
  IItemContaTransitoria,
  IContaConciliadaContaTransitoria,
  ILancamentoContaTransitoria,
} from '../interfaces/IAuditoriaContaTransitoria';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaContaTransitoriaResultado;
}

// ─── Painel de memória de cálculo detalhada ──────────────────────────────────

const estiloLinha: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  padding: '3px 0', borderBottom: '1px solid #fecaca', fontSize: 12,
};
const estiloRotulo: React.CSSProperties = { color: '#6b7280', minWidth: 140, flexShrink: 0 };
const estiloValor: React.CSSProperties = { fontWeight: 500, textAlign: 'right' };
const estiloContaTag: React.CSSProperties = {
  display: 'inline-block', background: '#f3f4f6', color: '#374151',
  fontSize: 11, padding: '1px 6px', borderRadius: 3, fontFamily: 'monospace',
};

const LinhaMemoria: React.FC<{ rotulo: string; valor: React.ReactNode }> = ({ rotulo, valor }) => (
  <div style={estiloLinha}>
    <span style={estiloRotulo}>{rotulo}</span>
    <span style={estiloValor}>{valor}</span>
  </div>
);

const badgeTipoOrigem = (tipo: string) => {
  const cores: Record<string, { bg: string; cor: string }> = {
    'Operacional': { bg: '#e0f2fe', cor: '#0369a1' },
    'Reclassificação': { bg: '#fef3c7', cor: '#92400e' },
    'Encerramento': { bg: '#f3e8ff', cor: '#7c3aed' },
  };
  const c = cores[tipo] ?? { bg: '#f3f4f6', cor: '#374151' };
  return (
    <span style={{ background: c.bg, color: c.cor, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3 }}>
      {tipo}
    </span>
  );
};

const MemoriaContaTransitoria: React.FC<{ item: IItemContaTransitoria }> = ({ item }) => {
  const temLancamentos = item.lancamentosFormadores?.length > 0;

  return (
    <div style={{
      background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6,
      padding: '10px 14px', marginTop: 8, fontSize: 12,
    }}>
      {/* Bloco: saldo do período */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#991b1b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Saldo do período — {item.competencia}
        </div>
        <LinhaMemoria
          rotulo="Saldo inicial"
          valor={
            <span>
              {formatCurrency(item.saldoInicial)}
              <span style={{ marginLeft: 4, ...estiloContaTag }}>{item.naturezaSaldoInicial}</span>
            </span>
          }
        />
        <LinhaMemoria rotulo="Débitos no período" valor={<span style={{ color: '#b91c1c' }}>{formatCurrency(item.totalDebitosDoMes)}</span>} />
        <LinhaMemoria rotulo="Créditos no período" valor={<span style={{ color: '#166534' }}>{formatCurrency(item.totalCreditosDoMes)}</span>} />
        <LinhaMemoria
          rotulo="Saldo final"
          valor={
            <strong style={{ color: '#991b1b' }}>
              {formatCurrency(Math.abs(item.saldoFinalMes))}
              <span style={{ marginLeft: 4, ...estiloContaTag }}>{item.naturezaSaldoFinal}</span>
            </strong>
          }
        />
      </div>

      {/* Bloco: lançamentos formadores */}
      {temLancamentos && (
        <div style={{ marginBottom: 8, borderTop: '1px dashed #fca5a5', paddingTop: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#991b1b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Lançamentos que formaram o saldo
          </div>
          {item.lancamentosFormadores.map((l: ILancamentoContaTransitoria, i: number) => (
            <div key={i} style={{ marginBottom: 8, paddingBottom: 6, borderBottom: '1px dotted #fecaca' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontWeight: 500 }}>
                  {formatDate(l.data)}
                  <span style={{ marginLeft: 6, ...estiloContaTag }}>{l.natureza}</span>
                  <span style={{ marginLeft: 8, color: l.natureza === 'D' ? '#b91c1c' : '#166534', fontWeight: 600 }}>
                    {formatCurrency(l.valor)}
                  </span>
                </span>
                {badgeTipoOrigem(l.tipoOrigem)}
              </div>
              {l.historico && (
                <div style={{ color: '#4b5563', marginBottom: 2, paddingLeft: 4 }}>
                  <em>{l.historico}</em>
                </div>
              )}
              {l.contaContraPartida && (
                <div style={{ paddingLeft: 4 }}>
                  <span style={{ color: '#6b7280' }}>Contrapartida: </span>
                  <span style={estiloContaTag}>{l.contaContraPartida}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bloco: resultado */}
      <div style={{ borderTop: '1px dashed #fca5a5', paddingTop: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#991b1b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Resultado
        </div>
        <LinhaMemoria rotulo="Conta" valor={<span style={estiloContaTag}>{item.contaContabil}</span>} />
        <LinhaMemoria rotulo="Natureza" valor="Transitória / Conta de passagem" />
        <LinhaMemoria
          rotulo="Status"
          valor={
            <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
              {item.status} — saldo não zerado no período
            </span>
          }
        />
        {item.memoriaCalculo && (
          <div style={{ marginTop: 6, color: '#6b7280', fontSize: 11 }}>
            <strong>Cálculo:</strong> {item.memoriaCalculo}
          </div>
        )}
      </div>
    </div>
  );
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaContaTransitoriaDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const saldoNaoZerado = resultado.saldoNaoZerado || [];
  const contasConciliadas = resultado.contasConciliadas || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Conciliação de Contas Transitórias — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFinanceiroTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Saldo Remanescente em Contas Transitórias</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          {resultado.impactoFinanceiroTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Auditoria de conciliação/encerramento — sem multa/imposto calculado.</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Meses com saldo não zerado em conta transitória no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada a esta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Saldo Não Zerado em Contas Transitórias</h3>
        <p className='text-muted'>
          Meses em que uma conta transitória/de passagem terminou com saldo diferente de zero — toda conta transitória
          deve ser zerada na conciliação mensal (IN RFB nº 2.003/2021; CPC 00). Clique para ver o detalhe.
        </p>

        {saldoNaoZerado.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma conta transitória com saldo não zerado identificada no período.</div>
        )}

        {saldoNaoZerado.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Competência</th>
                  <th className='text-end'>Saldo Pendente</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {saldoNaoZerado.map((item: IItemContaTransitoria, idx) => {
                  const chave = `ct-${item.empresaId}-${item.contaContabil}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.competencia}</td>
                        <td className='text-end'>{formatCurrency(Math.abs(item.saldoFinalMes))}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fef2f2' }}>
                            <div className='py-2 px-3'>
                              <div style={{ fontSize: 12, color: '#991b1b', marginBottom: 4 }}>
                                <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                              </div>
                              <MemoriaContaTransitoria item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {contasConciliadas.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Contas Transitórias Verificadas e Conciliadas
          </h3>
          <p className='text-muted'>
            Contas transitórias/de passagem que foram auditadas e estão em conformidade — saldo zerado em todos os meses
            do período.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th className='text-end'>Saldo Final do Período</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasConciliadas.map((item: IContaConciliadaContaTransitoria, idx) => {
                  const chave = `cc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoFinalPeriodo)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={3} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' /><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaContaTransitoriaDetalhe;
